import type { RootState } from 'redux/store'

import { createSelector } from '@reduxjs/toolkit'
import { appSelectors } from 'redux/reducers/app'

import type { Conversation, Message } from '../model/types'

import { api } from '../api'
import { slice } from '../model/slice'
import * as adapters from './adapters'
import { createMessageMap } from './utils'

const simpleSelectors = slice.selectors

const getRoot = (state: RootState) => state
const placeholderId = (id: string) => id

const createConversationsSelector = createSelector(
	simpleSelectors.filter,
	simpleSelectors.search,
	(filter, search) => api.endpoints.conversations.select({ filter, search })
)

const selectConversations = createSelector(
	getRoot,
	createConversationsSelector,
	(root, select) => {
		let nextCursor = ''
		const conversations: Conversation[] = []

		const { data, isUninitialized, originalArgs } = select(root)
		if (!isUninitialized && data) {
			nextCursor = data?.nextCursor ?? ''
			conversations.push(...data.items)

			if (nextCursor) {
				function checkNext() {
					const select = api.endpoints.conversationsNext.select({
						...originalArgs!,
						nextCursor
					})

					const { data, isUninitialized } = select(root)

					if (!isUninitialized && data) {
						conversations.push(...data.items)
						nextCursor = data.nextCursor ?? ''

						if (nextCursor) {
							checkNext()
						}
					}
				}

				checkNext()
			}
		}

		return { conversations, nextCursor }
	}
)

const selectParticipants = createSelector(placeholderId, id =>
	api.endpoints.participants.select(id)
)

const selectConversationalist = createSelector(
	appSelectors.personIdLi,
	(state: RootState, conversation: string) =>
		selectParticipants(conversation)(state),
	(id, { data: participants }) =>
		participants?.find(participant => participant.entityId !== id)
)

const createMessagesSelector = createSelector(placeholderId, id =>
	api.endpoints.messages.select(id)
)

const selectConversation = createSelector(
	getRoot,
	(_: RootState, id: string) => createMessagesSelector(id),
	(root, select) => {
		const messages: Message[] = []
		let prevCursor = ''

		const { data, isUninitialized, originalArgs: id } = select(root)
		if (!isUninitialized && data) {
			messages.push(...data.items)

			prevCursor = data.prevCursor ?? ''
			if (prevCursor) {
				function checkPrev() {
					const select = api.endpoints.messagesPrev.select({
						id: id!,
						prevCursor
					})

					const { data, isUninitialized } = select(root)

					if (!isUninitialized && data) {
						messages.push(...data.items)
						prevCursor = data.prevCursor ?? ''

						if (prevCursor) {
							checkPrev()
						}
					}
				}

				checkPrev()
			}
		}

		return { ...createMessageMap(messages), prevCursor }
	}
)

export const selectors = {
	...simpleSelectors,
	conversation: selectConversation,
	conversationalist: selectConversationalist,
	conversations: adapters.conversations.getSelectors(
		simpleSelectors.conversations
	),
	messages: adapters.messages.getSelectors(simpleSelectors.messages),
	showedConversations: selectConversations
}
